import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import api from '../api';
import ChatEmbedWidget from './ChatEmbedWidget';
import VoiceCallWidget from './VoiceCallWidget';
import { MessageSquare, Phone, X, Minimize2, Loader2 } from 'lucide-react';
import { restoreLauncherState, saveLauncherState, noteLeft } from './chatSessionStore';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  return result
    ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`
    : '209, 18, 66';
};

const positionStyles = (position, margin = '1.5rem') => {
  const map = {
    'bottom-right': { bottom: margin, right: margin },
    'bottom-left': { bottom: margin, left: margin },
    'top-right': { top: margin, right: margin },
    'top-left': { top: margin, left: margin },
  };
  return map[position] || map['bottom-right'];
};

// Fixed launcher-panel width used in floating-button mode. Kept module-level so
// both the render and the parent-resize reporting effect share one source of truth.
const V2_PANEL_WIDTH = 340;

// postMessage type the lightweight loader (public/combo-widget-v2.js) listens for
// to resize/reposition the floating-button iframe to the widget's real footprint.
const V2_RESIZE_MESSAGE = 'braincx-combo-widget-v2-resize';

// Inbound command from the host page, forwarded by the loader's BrainCXCombo API
// (the host page cannot click the launcher directly — it lives in this iframe's
// document). V2_READY_MESSAGE tells the loader this listener is live so it can
// flush a command that was issued before the widget mounted.
const V2_COMMAND_MESSAGE = 'braincx-combo-widget-v2-command';
const V2_READY_MESSAGE = 'braincx-combo-widget-v2-ready';

// Transparent "bleed" padded around the closed launcher inside the embed iframe.
// The iframe hugs the launcher's layout box, but the pulse ring (a scale(2.1)
// transform, invisible to layout) and the teaser bubble's drop shadow render
// OUTSIDE that box and would be clipped by the iframe edge. This padding gives
// them room; the reported corner margin is reduced by the same amount so the
// button still sits ~24px from the screen corner. Mirrors ChatEmbedButton.
const getClosedBleed = (buttonPx, settings) => {
  if (!settings) return 0;
  if (settings.pulseAnimation) return Math.ceil(buttonPx * 0.45);
  if (settings.showLabel) return 16;
  return 0;
};

const LAUNCHER_SIZES = {
  small: { button: 52, icon: 20, logo: 44, badge: 14, panelGap: '3.75rem' },
  medium: { button: 64, icon: 26, logo: 54, badge: 16, panelGap: '4.5rem' },
  large: { button: 76, icon: 32, logo: 64, badge: 18, panelGap: '5.25rem' },
};

/** Hide panel visually while keeping children mounted (active voice calls) */
const PANEL_KEEP_ALIVE_STYLE = {
  position: 'fixed',
  left: 0,
  top: 0,
  width: 2,
  height: 2,
  opacity: 0,
  pointerEvents: 'none',
  overflow: 'hidden',
  zIndex: -1,
};

const getSettingsFromAgentV2 = (agent, urlParams) => {
  const p = urlParams || new URLSearchParams();
  const hexParam = (name) => (p.get(name) ? `#${p.get(name).replace('#', '')}` : null);
  return {
    logoUrl: agent?.combo_widget_v2_logo_url || null,
    bgColor: hexParam('bg') || agent?.combo_widget_v2_bg_color || '#020e1e',
    surfaceColor: hexParam('surface') || agent?.combo_widget_v2_surface_color || '#0a3161',
    accentColor: hexParam('accent') || agent?.combo_widget_v2_accent_color || '#769fc8',
    launcherColor: hexParam('launcher') || agent?.combo_widget_v2_launcher_color || '#d11242',
    launcherSize: LAUNCHER_SIZES[p.get('launcherSize')] ? p.get('launcherSize') : (LAUNCHER_SIZES[agent?.combo_widget_v2_launcher_size] ? agent.combo_widget_v2_launcher_size : 'medium'),
    position: p.get('position') || agent?.combo_widget_v2_button_position || 'bottom-right',
    buttonLabel: agent?.combo_widget_v2_button_label || 'Contact us',
    title: agent?.combo_widget_v2_title || agent?.name || 'Assistant',
    subtitle: agent?.combo_widget_v2_subtitle || 'Talk or type — your call.',
    tagline: agent?.combo_widget_v2_tagline || 'Real people. Real answers. Right now.',
    teaserMessage: agent?.combo_widget_v2_teaser_message || "Need help? We're online now.",
    defaultChannel: p.get('channel') || agent?.combo_widget_v2_default_channel || 'chat',
    pulseAnimation: agent?.combo_widget_v2_pulse_animation ?? true,
    showLabel: agent?.combo_widget_v2_show_label ?? true,
    showChannelSwitch: agent?.combo_widget_v2_show_channel_switch ?? true,
    disclaimerEnabled: agent?.combo_widget_v2_disclaimer_enabled ?? false,
    disclaimerText: agent?.combo_widget_v2_disclaimer_text || '',
    disclaimerLinks: (Array.isArray(agent?.combo_widget_v2_disclaimer_links) && agent.combo_widget_v2_disclaimer_links.length > 0)
      ? agent.combo_widget_v2_disclaimer_links.filter((l) => l?.text && l?.url)
      : (agent?.combo_widget_v2_disclaimer_link_text && agent?.combo_widget_v2_disclaimer_link_url)
        ? [{ text: agent.combo_widget_v2_disclaimer_link_text, url: agent.combo_widget_v2_disclaimer_link_url }]
        : [],
  };
};

// ─── Small UI pieces ──────────────────────────────────────────────────────────

const AvatarMark = ({ logoUrl, size, surfaceColor, radius }) => (
  <div
    style={{
      width: size,
      height: size,
      borderRadius: radius,
      background: surfaceColor,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      overflow: 'hidden',
    }}
  >
    {logoUrl ? (
      <img src={logoUrl} alt="" style={{ width: '82%', height: '82%', objectFit: 'contain' }} />
    ) : (
      <MessageSquare size={Math.round(size * 0.5)} color="#f4f5f5" />
    )}
  </div>
);

const PanelHeader = ({ settings, onClose }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '16px 18px',
      flexShrink: 0,
      gap: 12,
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
      <AvatarMark logoUrl={settings.logoUrl} size={38} radius={11} surfaceColor={settings.surfaceColor} />
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            color: '#f4f5f5',
            fontWeight: 700,
            fontSize: 14,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {settings.title}
        </div>
        <div style={{ color: '#53b59e', fontSize: 11.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 5, marginTop: 1 }}>
          <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#53b59e', display: 'inline-block' }} />
          Online now
        </div>
      </div>
    </div>
    {/* Collapses the panel back to the launcher. It ends nothing - the
        conversation is still there when the visitor reopens it - so it carries
        the minimize icon rather than a cross, matching the Chat embed. */}
    <button
      type="button"
      onClick={onClose}
      aria-label="Minimize"
      style={{ background: 'transparent', border: 'none', color: '#6B7689', cursor: 'pointer', padding: 4, display: 'flex', flexShrink: 0 }}
    >
      <Minimize2 size={20} />
    </button>
  </div>
);

const ActiveCallStatusBar = ({ onClose }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      padding: '10px 18px 4px',
      flexShrink: 0,
    }}
  >
    {/* A cross read as "hang up" here, which is the one thing this does not do:
        the panel stays mounted and the call runs on in the background, with the
        launcher showing a green badge to return to it. */}
    <button
      type="button"
      onClick={onClose}
      aria-label="Minimize"
      title="Minimize, the call keeps running"
      style={{ background: 'transparent', border: 'none', color: '#6B7689', cursor: 'pointer', padding: 4, display: 'flex', flexShrink: 0 }}
    >
      <Minimize2 size={18} />
    </button>
  </div>
);

const ChannelTabs = ({ channel, onChange, settings }) => (
  <div style={{ padding: '0 14px 12px', flexShrink: 0 }}>
    <div
      style={{ display: 'flex', gap: 4, background: settings.surfaceColor, padding: 4, borderRadius: 12 }}
      role="tablist"
      aria-label="Switch between message and voice"
    >
      {[
        { id: 'chat', label: 'Message', Icon: MessageSquare },
        { id: 'voice', label: 'Voice', Icon: Phone },
      ].map(({ id, label, Icon }) => (
        <button
          key={id}
          type="button"
          role="tab"
          aria-selected={channel === id}
          onClick={() => onChange(id)}
          style={{
            flex: 1,
            border: 'none',
            borderRadius: 9,
            padding: '9px',
            fontFamily: 'inherit',
            fontWeight: 700,
            fontSize: 13,
            cursor: 'pointer',
            background: channel === id ? settings.accentColor : 'transparent',
            color: channel === id ? '#020e1e' : `${settings.accentColor}b3`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            transition: 'background 0.2s ease, color 0.2s ease',
          }}
        >
          <Icon size={14} />
          {label}
        </button>
      ))}
    </div>
  </div>
);

// Turns matching words/phrases inside disclaimerText into inline underlined links.
// Falls back to appending a link after the text when its phrase isn't found in the sentence
// (covers agents configured before inline linking existed).
const renderDisclaimer = (text, links, accentColor) => {
  const validLinks = (links || []).filter((l) => l?.text?.trim() && l?.url?.trim());
  if (!text || validLinks.length === 0) return text;

  const ranges = [];
  validLinks.forEach((link) => {
    const idx = text.indexOf(link.text);
    if (idx === -1) return;
    const end = idx + link.text.length;
    const overlaps = ranges.some((r) => idx < r.end && end > r.start);
    if (!overlaps) ranges.push({ start: idx, end, link });
  });

  const unmatchedLinks = validLinks.filter((link) => !ranges.some((r) => r.link === link));

  if (ranges.length === 0) {
    return (
      <>
        {text}
        {' '}
        {unmatchedLinks.map((link, index) => (
          <React.Fragment key={index}>
            {index > 0 && ' · '}
            <a href={link.url} target="_blank" rel="noopener noreferrer" style={{ color: accentColor, textDecoration: 'underline' }}>
              {link.text}
            </a>
          </React.Fragment>
        ))}
      </>
    );
  }

  ranges.sort((a, b) => a.start - b.start);
  const nodes = [];
  let cursor = 0;
  ranges.forEach((r, i) => {
    if (r.start > cursor) nodes.push(text.slice(cursor, r.start));
    nodes.push(
      <a key={i} href={r.link.url} target="_blank" rel="noopener noreferrer" style={{ color: accentColor, textDecoration: 'underline' }}>
        {text.slice(r.start, r.end)}
      </a>
    );
    cursor = r.end;
  });
  if (cursor < text.length) nodes.push(text.slice(cursor));

  return (
    <>
      {nodes}
      {unmatchedLinks.length > 0 && (
        <>
          {' '}
          {unmatchedLinks.map((link, index) => (
            <React.Fragment key={index}>
              {index > 0 && ' · '}
              <a href={link.url} target="_blank" rel="noopener noreferrer" style={{ color: accentColor, textDecoration: 'underline' }}>
                {link.text}
              </a>
            </React.Fragment>
          ))}
        </>
      )}
    </>
  );
};

const IntroScreen = ({ settings, onSelectChat, onSelectVoice }) => (
  <div style={{ padding: '4px 18px 24px', flex: 1, overflowY: 'auto' }}>
    <div style={{ color: '#f4f5f5', fontWeight: 700, fontSize: 22, lineHeight: 1.2, marginBottom: 6, letterSpacing: '-0.01em' }}>
      {settings.subtitle}
    </div>
    <div style={{ color: settings.accentColor, fontSize: 13, marginBottom: 20 }}>{settings.tagline}</div>
    <button
      type="button"
      onClick={onSelectVoice}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        width: '100%',
        background: settings.accentColor,
        border: 'none',
        borderRadius: 14,
        padding: 15,
        cursor: 'pointer',
        marginBottom: 11,
      }}
    >
      <Phone size={16} color="#020e1e" />
      <span style={{ color: '#020e1e', fontWeight: 700, fontSize: 14.5 }}>Start a voice call</span>
    </button>
    <button
      type="button"
      onClick={onSelectChat}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        width: '100%',
        background: 'transparent',
        border: `1.5px solid ${settings.surfaceColor}`,
        borderRadius: 14,
        padding: 15,
        cursor: 'pointer',
      }}
    >
      <MessageSquare size={16} color="#f4f5f5" />
      <span style={{ color: '#f4f5f5', fontWeight: 700, fontSize: 14.5 }}>Send a message</span>
    </button>
    {settings.disclaimerEnabled && settings.disclaimerText && (
      <div
        style={{
          color: `${settings.accentColor}b3`,
          fontSize: 11.5,
          lineHeight: 1.5,
          marginTop: 14,
          textAlign: 'center',
        }}
      >
        {renderDisclaimer(settings.disclaimerText, settings.disclaimerLinks, settings.accentColor)}
      </div>
    )}
  </div>
);

// ─── Main shell (shared by button / inline / full view modes) ────────────────

const ComboWidgetV2Shell = ({
  agentId,
  agent,
  settings,
  channel,
  hasStarted,
  onSelectChannel,
  onTabChange,
  onClose,
  embedToken,
  voiceRef,
  onVoiceStateChange,
  voiceConnected,
  voiceConnecting,
}) => {
  const showActiveCallBar = channel === 'voice' && hasStarted && (voiceConnected || voiceConnecting);
  return (
    <div
      className="flex flex-col overflow-hidden h-full"
      style={{ backgroundColor: settings.bgColor }}
    >
      {showActiveCallBar
        ? <ActiveCallStatusBar onClose={onClose} />
        : <PanelHeader settings={settings} onClose={onClose} />}

      {hasStarted && settings.showChannelSwitch && (
        <ChannelTabs channel={channel} onChange={onTabChange} settings={settings} />
      )}

      <div className="flex-1 min-h-0 relative overflow-hidden" style={{ display: 'flex', flexDirection: 'column' }}>
        {!hasStarted && (
          <IntroScreen
            settings={settings}
            onSelectChat={() => onSelectChannel('chat')}
            onSelectVoice={() => onSelectChannel('voice')}
          />
        )}

        <div
          style={{ position: 'absolute', inset: 0, display: hasStarted && channel === 'chat' ? 'block' : 'none' }}
        >
          <ChatEmbedWidget
            agentId={agentId}
            embedToken={embedToken}
            onClose={() => {}}
            themeOverride={{
              mode: 'dark',
              primaryColor: settings.accentColor,
              bgColor: settings.bgColor,
              surfaceColor: settings.surfaceColor,
              userTextColor: '#020e1e',
              hideAvatars: true,
            }}
          />
        </div>

        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: hasStarted && channel === 'voice' ? 'flex' : 'none',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <VoiceCallWidget
            ref={voiceRef}
            agentId={agentId}
            viewMode="panel"
            hideHeader
            themeOverride={{
              mode: 'dark',
              primaryColor: settings.accentColor,
              buttonColor: settings.accentColor,
              avatarColor: settings.surfaceColor,
              showTranscript: agent?.voice_widget_show_transcript ?? true,
              bgColor: settings.bgColor,
              bgColor2: settings.surfaceColor,
              logoUrl: settings.logoUrl,
              title: settings.title,
            }}
            onCallStateChange={onVoiceStateChange}
          />
        </div>
      </div>
    </div>
  );
};

const V2_INTRO_H = 380;
// Matches ChatEmbedButton's expanded chat-interface height (expandedHeight = 520)
// so the Combo Widget V2 chat panel isn't taller than the standalone Chat embed.
const V2_CHAT_H = 520;

const getPanelHeightV2 = (screen, hasStarted, agent, voiceConnected, voiceConnecting) => {
  if (!hasStarted) {
    const hasDisclaimer = Boolean(agent?.combo_widget_v2_disclaimer_enabled && agent?.combo_widget_v2_disclaimer_text?.trim());
    return hasDisclaimer ? V2_INTRO_H + 50 : V2_INTRO_H;
  }
  if (screen === 'chat') return V2_CHAT_H;
  const showTranscript = agent?.voice_widget_show_transcript ?? true;
  const hasWelcome = Boolean(agent?.voice_widget_welcome_message?.trim());
  if (showTranscript && voiceConnected) return V2_CHAT_H;
  if (!showTranscript) {
    if (voiceConnected) return 460;
    if (voiceConnecting) return 300;
    return hasWelcome ? 300 : 260;
  }
  if (voiceConnecting) return 280;
  return hasWelcome ? 300 : 260;
};

// ─── Floating launcher + expanded panel ───────────────────────────────────────

const ComboWidgetV2 = ({ agentId, viewMode: initialViewMode }) => {
  // The widget lives in an iframe on the customer's site, so every page the
  // visitor clicks through to remounts it from zero. Seeding the launcher from
  // the stored conversation is what makes a chat survive that: without it the
  // visitor came back to a closed launcher that reopened on the channel picker,
  // which read as the session having ended. Resolved once per mount - see
  // chatSessionStore for the resume window and for what is deliberately not
  // restored (a voice call, an unanswered greeting).
  const restoredUi = useRef(null);
  if (restoredUi.current === null) restoredUi.current = restoreLauncherState(agentId);

  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(restoredUi.current.isOpen);
  const [channel, setChannel] = useState(restoredUi.current.channel || 'chat');
  const [hasStarted, setHasStarted] = useState(restoredUi.current.hasStarted);
  const [voiceConnected, setVoiceConnected] = useState(false);
  const [voiceConnecting, setVoiceConnecting] = useState(false);
  const [settings, setSettings] = useState(null);
  const voiceRef = useRef(null);
  const embedTokenRef = useRef(null);
  const engagedRef = useRef(restoredUi.current.inConversation);
  const startRetryRef = useRef(0);
  // Refs to the launcher container and the expanded panel, measured to report
  // the widget's real footprint to the parent page's loader script (embed only).
  const launcherRef = useRef(null);
  const panelRef = useRef(null);

  // True when rendered inside the lightweight-loader iframe (view=button embed),
  // as opposed to a top-level "Preview" tab. When embedded, the widget sits
  // flush against its own iframe edges (margin 0) and the parent loader script
  // handles the screen-corner margin + viewport clamping — see the resize effect
  // below. Mirrors ChatEmbedButton's embed behavior.
  const isEmbedded = typeof window !== 'undefined' && window.parent !== window;

  // Only the lightweight loader (public/combo-widget-v2.js) adds `embed=loader`,
  // and it is the only host that listens for our resize messages. Gate the
  // flush-to-iframe layout + resize reporting on it so the standalone raw-iframe
  // embed, Preview tabs, and any hand-rolled iframe keep their original look.
  const usesLoader =
    isEmbedded &&
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('embed') === 'loader';

  // `pointer:coarse` reflects the real input hardware (unaffected by this
  // iframe's own size), so it's a reliable phone/tablet check. On touch we use a
  // slightly narrower open panel and smaller corner margin so the panel fits
  // common phone widths without the parent loader clamping (and clipping) it.
  // Mirrors ChatEmbedButton's touch handling.
  const isTouchDevice =
    typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

  const [viewMode] = useState(() => {
    if (initialViewMode) return initialViewMode;
    if (typeof window === 'undefined') return 'button';
    const m = new URLSearchParams(window.location.search).get('view');
    if (m === 'full') return 'full';
    if (m === 'inline' || m === 'minimal') return 'inline';
    return 'button';
  });

  // Launcher look derived from URL params only, so the button renders in the
  // right spot/color/size immediately -- before the agent fetch resolves.
  const loadingLauncher = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    const hexParam = (name) => (p.get(name) ? `#${p.get(name).replace('#', '')}` : null);
    return {
      launcherColor: hexParam('launcher') || '#d11242',
      launcherSize: LAUNCHER_SIZES[p.get('launcherSize')] ? p.get('launcherSize') : 'medium',
      position: p.get('position') || 'bottom-right',
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token') || params.get('embed_token') || params.get('key');
    if (t) embedTokenRef.current = t;
  }, []);

  // Keep the stored launcher state in step with the live one so the next page
  // can restore it. Only the floating-button embed has a launcher to restore;
  // inline and full-page views are always visible and own no open/closed state.
  // Nothing is written until the visitor actually opens the widget, so a
  // passerby who never touches it leaves no trace in their browser.
  useEffect(() => {
    if (!agentId || viewMode !== 'button') return;
    if (isOpen || hasStarted) engagedRef.current = true;
    if (!engagedRef.current) return;
    saveLauncherState(agentId, { isOpen, hasStarted, channel });
  }, [agentId, viewMode, isOpen, hasStarted, channel]);

  // pagehide also fires when this frame is unloaded by the host page navigating,
  // which is the case the whole resume mechanism exists for.
  useEffect(() => {
    if (!agentId) return;
    const handleLeave = () => noteLeft(agentId);
    window.addEventListener('pagehide', handleLeave);
    window.addEventListener('beforeunload', handleLeave);
    return () => {
      window.removeEventListener('pagehide', handleLeave);
      window.removeEventListener('beforeunload', handleLeave);
    };
  }, [agentId]);

  useEffect(() => {
    if (!agentId) { setLoading(false); return; }
    api.get(`/agents/${agentId}`)
      .then(r => {
        setAgent(r.data);
        const s = getSettingsFromAgentV2(r.data);
        setSettings(s);
      })
      .catch(() => setError('Agent not found'))
      .finally(() => setLoading(false));
  }, [agentId]);

  useEffect(() => {
    if (viewMode !== 'button') return;
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    const prev = [html.style.background, body.style.background, root?.style?.background];
    html.style.background = 'transparent';
    body.style.background = 'transparent';
    if (root) root.style.background = 'transparent';
    return () => {
      html.style.background = prev[0];
      body.style.background = prev[1];
      if (root) root.style.background = prev[2] || '';
    };
  }, [viewMode]);

  // Report the widget's real rendered footprint to the parent page so the
  // lightweight loader script can shrink/grow and reposition the iframe to match
  // exactly. A fixed-size iframe would otherwise sit over the page (blocking
  // clicks and :hover on whatever is underneath) even while the widget is closed.
  // Only runs in the embedded floating-button view; Preview tabs, inline and
  // full-page embeds are untouched. Mirrors ChatEmbedButton's resize reporting.
  useEffect(() => {
    if (!usesLoader || viewMode !== 'button') return;

    const send = () => {
      const launcherEl = launcherRef.current;
      if (!launcherEl) return;
      const conf =
        LAUNCHER_SIZES[(settings && settings.launcherSize) || loadingLauncher.launcherSize] ||
        LAUNCHER_SIZES.medium;
      const position = (settings && settings.position) || loadingLauncher.position;

      // Use offsetWidth/Height (untransformed layout box), NOT
      // getBoundingClientRect — the launcher pulse ring (scale 2.1) and the
      // panel slide-up (scale 0.96 -> 1) are transforms that would report a
      // mid-animation size the parent then locks the iframe to, clipping the
      // widget until a later re-render.
      let width = launcherEl.offsetWidth;
      let height = launcherEl.offsetHeight;

      // Open: the panel floats above the still-visible launcher, so the real
      // footprint is the union of both boxes measured from the anchored corner
      // (the panel sits `panelGap` above the corner). Launcher stays 24px from
      // the corner. Closed: only the launcher container, which we bleed outward
      // (see closedBleed in the render) for the pulse ring + teaser shadow and
      // then reduce the reported margin by that bleed so the button still lands
      // ~24px from the screen corner.
      let margin;
      if (isOpen) {
        // Open: the launcher + panel are inset by `inset` inside the iframe (see
        // launcherMargin in the render), and the iframe sits flush to the screen
        // corner (margin 0). The reported footprint therefore includes that inset
        // so the loader's iframe extends into the corner gap for the glow shadow.
        const inset = isTouchDevice ? 12 : 24;
        const gapPx = parseFloat(conf.panelGap) * 16;
        const panelEl = panelRef.current;
        const panelH = panelEl
          ? panelEl.offsetHeight
          : getPanelHeightV2(channel, hasStarted, agent, voiceConnected, voiceConnecting);
        const panelW = panelEl ? panelEl.offsetWidth : V2_PANEL_WIDTH;
        width = inset + Math.max(width, panelW);
        height = inset + Math.max(height, gapPx + panelH);
        margin = 0;
      } else {
        const bleed = getClosedBleed(conf.button, settings);
        margin = Math.max(4, 24 - bleed);
      }

      window.parent.postMessage(
        {
          type: V2_RESIZE_MESSAGE,
          width: Math.ceil(width),
          height: Math.ceil(height),
          margin,
          position,
          centered: false,
        },
        '*'
      );
    };

    send();
    const observer = new ResizeObserver(send);
    if (launcherRef.current) observer.observe(launcherRef.current);
    if (panelRef.current) observer.observe(panelRef.current);
    return () => observer.disconnect();
  }, [
    usesLoader,
    isTouchDevice,
    viewMode,
    isOpen,
    hasStarted,
    channel,
    voiceConnected,
    voiceConnecting,
    loading,
    settings,
    agent,
    loadingLauncher,
  ]);

  // Host-page command channel. Answers BrainCXCombo.open()/close()/toggle() by
  // setting the very same `isOpen` state the launcher's own onClick sets, so the
  // resize reporting effect above recalculates and republishes the footprint
  // through its existing path — a programmatic open is indistinguishable from a
  // click as far as sizing and positioning are concerned. Nothing else about the
  // widget's state is touched: a visitor who already picked chat or voice comes
  // back to their conversation, and a fresh visitor lands on the choose-a-channel
  // screen, exactly as when clicking the launcher.
  useEffect(() => {
    if (viewMode !== 'button') return;
    if (typeof window === 'undefined' || !window.parent || window.parent === window) return;

    const onMessage = (event) => {
      // Only from our direct embedder, and only the exact namespaced type. Host
      // pages (WordPress page builders, video embeds, analytics) post plenty of
      // unrelated message traffic that must never pop the widget open.
      if (event.source !== window.parent) return;
      const data = event.data;
      if (!data || typeof data !== 'object' || data.type !== V2_COMMAND_MESSAGE) return;
      if (data.action === 'open') setIsOpen(true);
      else if (data.action === 'close') setIsOpen(false);
      else if (data.action === 'toggle') setIsOpen((prev) => !prev);
    };

    window.addEventListener('message', onMessage);
    window.parent.postMessage({ type: V2_READY_MESSAGE }, '*');
    return () => window.removeEventListener('message', onMessage);
  }, [viewMode]);

  const tryStartVoice = useCallback(() => {
    const v = voiceRef.current;
    if (!v) return;
    if (v.isConnected || v.isConnecting) return;
    if (v.isReady) {
      startRetryRef.current = 0;
      v.startCall();
    } else if (startRetryRef.current < 12) {
      startRetryRef.current += 1;
      setTimeout(tryStartVoice, 250);
    }
  }, []);

  const handleSelectChannel = useCallback((newChannel) => {
    setHasStarted(true);
    setChannel(newChannel);
    if (newChannel === 'voice') tryStartVoice();
  }, [tryStartVoice]);

  // Switching to Chat while a call is live leaves it running in the background —
  // flipping back to Voice resumes the same connected call, but never auto-dials
  // a new one on a bare tab switch.
  const handleTabChange = useCallback((newChannel) => {
    if (newChannel === channel) return;
    setChannel(newChannel);
  }, [channel]);

  const handleClose = () => setIsOpen(false);

  if (loading || !settings) {
    if (viewMode !== 'button') {
      return (
        <div className="flex items-center justify-center p-8">
          <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#769fc8' }} />
        </div>
      );
    }
    // Show the launcher circle itself (spinner in place of the icon) instead of
    // filling the whole embed with a loading block while the agent config loads.
    const lPos = positionStyles(loadingLauncher.position, usesLoader ? '0px' : '1.5rem');
    const lSize = LAUNCHER_SIZES[loadingLauncher.launcherSize] || LAUNCHER_SIZES.medium;
    return (
      <div ref={launcherRef} className="fixed z-[9999999]" style={lPos}>
        <div
          style={{
            width: lSize.button,
            height: lSize.button,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: loadingLauncher.launcherColor,
            boxShadow: `0 16px 30px -8px rgba(${hexToRgb(loadingLauncher.launcherColor)},0.6)`,
          }}
        >
          <Loader2 className="animate-spin" style={{ width: lSize.icon, height: lSize.icon, color: '#f4f5f5' }} />
        </div>
      </div>
    );
  }

  if (error) {
    return <div className="p-6 text-center text-sm" style={{ color: '#d11242' }}>{error}</div>;
  }

  // When open under the loader we inset the widget by the design margin INSIDE
  // its iframe and report margin:0, so the iframe reaches the screen corner and
  // the launcher's glow shadow bleeds into that corner gap (cut naturally by the
  // screen edge) instead of being hard-clipped at the iframe edge. Touch gets a
  // tighter inset. Closed keeps the tight, non-blocking bleed footprint.
  const openInset = isTouchDevice ? 12 : 24;
  const launcherMargin = usesLoader ? (isOpen ? `${openInset}px` : '0px') : '1.5rem';
  const pos = positionStyles(settings.position, launcherMargin);
  const launcherRgb = hexToRgb(settings.launcherColor);
  const sizeConf = LAUNCHER_SIZES[settings.launcherSize] || LAUNCHER_SIZES.medium;
  const sessionActive = voiceConnected || voiceConnecting;
  // Transparent room for the pulse ring / teaser shadow when loaded + closed;
  // the loader reduces the corner margin by this so the button stays ~24px in.
  const closedBleed = usesLoader && !isOpen ? getClosedBleed(sizeConf.button, settings) : 0;

  const shellProps = {
    agentId,
    agent,
    settings,
    channel,
    hasStarted,
    onSelectChannel: handleSelectChannel,
    onTabChange: handleTabChange,
    onClose: handleClose,
    embedToken: embedTokenRef.current,
    voiceRef,
    voiceConnected,
    voiceConnecting,
    onVoiceStateChange: ({ isConnected, isConnecting }) => {
      setVoiceConnected(isConnected);
      setVoiceConnecting(isConnecting);
    },
  };

  // Full-page embed
  if (viewMode === 'full') {
    const fullHeight = getPanelHeightV2(channel, hasStarted, agent, voiceConnected, voiceConnecting);
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        style={{ background: 'linear-gradient(135deg, #020e1e, #0a3161)' }}
      >
        <div
          className="w-full max-w-md rounded-2xl overflow-hidden shadow-2xl transition-all duration-300"
          style={{ height: fullHeight }}
        >
          <ComboWidgetV2Shell {...shellProps} />
        </div>
      </div>
    );
  }

  // Inline / minimal embed
  if (viewMode === 'inline') {
    const inlineHeight = getPanelHeightV2(channel, hasStarted, agent, voiceConnected, voiceConnecting);
    return (
      <div className="w-full rounded-2xl overflow-hidden transition-all duration-300" style={{ height: inlineHeight }}>
        <ComboWidgetV2Shell {...shellProps} />
      </div>
    );
  }

  // Floating button mode
  const panelHeight = getPanelHeightV2(channel, hasStarted, agent, voiceConnected, voiceConnecting);
  // Narrow the panel a touch on phones so the parent loader's viewport clamp
  // doesn't shave the edge off on ~360px screens (matches ChatEmbedButton).
  const panelWidth = usesLoader && isTouchDevice ? 300 : V2_PANEL_WIDTH;

  return (
    <>
      <style>{`
        @keyframes comboV2PulseRing {
          0% { transform: scale(.85); opacity: .55; }
          100% { transform: scale(2.1); opacity: 0; }
        }
        @keyframes comboV2SlideUp {
          from { opacity: 0; transform: translateY(16px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes comboV2Pop {
          0% { transform: scale(0.85); opacity: 0; }
          60% { transform: scale(1.08); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes comboV2Floaty {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
      `}</style>

      {(isOpen || sessionActive) && (
        <div
          ref={panelRef}
          style={
            isOpen
              ? {
                  position: 'fixed',
                  zIndex: 9999998,
                  // When embedded the parent loader clamps the iframe to the real
                  // viewport, so use a fixed panel size here (a min()/calc(100vh)
                  // would resolve against the iframe's own size — which the parent
                  // sets from what we report — creating a feedback loop). In a
                  // top-level Preview tab we keep the viewport-relative clamp.
                  width: usesLoader ? `${panelWidth}px` : `min(${panelWidth}px, calc(100vw - 2rem))`,
                  height: usesLoader ? `${panelHeight}px` : `min(${panelHeight}px, calc(100vh - 6rem))`,
                  ...pos,
                  ...(settings.position.startsWith('bottom')
                    ? { bottom: `calc(${pos.bottom || '1.5rem'} + ${sizeConf.panelGap})` }
                    : { top: `calc(${pos.top || '1.5rem'} + ${sizeConf.panelGap})` }),
                  borderRadius: 22,
                  overflow: 'hidden',
                  boxShadow: '0 26px 60px -22px rgba(2,14,30,0.6)',
                  animation: 'comboV2SlideUp 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
                  transition: 'height 0.3s ease, width 0.3s ease',
                }
              : PANEL_KEEP_ALIVE_STYLE
          }
          aria-hidden={!isOpen}
        >
          <ComboWidgetV2Shell {...shellProps} />
        </div>
      )}

      <div ref={launcherRef} className="fixed z-[9999999] flex flex-col gap-2 items-end w-max" style={{ ...pos, padding: closedBleed }}>
        {settings.showLabel && !isOpen && (
          <div
            style={{
              background: settings.bgColor,
              color: '#f4f5f5',
              padding: '13px 16px',
              borderRadius: 16,
              borderBottomRightRadius: 5,
              fontSize: 12.5,
              lineHeight: 1.45,
              maxWidth: 205,
              boxShadow: '0 18px 34px -12px rgba(2,14,30,.45)',
              animation: 'comboV2Floaty 4s ease-in-out infinite',
            }}
          >
            {settings.teaserMessage}
          </div>
        )}
        <div style={{ position: 'relative', width: sizeConf.button, height: sizeConf.button }}>
          {settings.pulseAnimation && !isOpen && (
            <span
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '50%',
                background: settings.launcherColor,
                animation: 'comboV2PulseRing 2.4s ease-out infinite',
              }}
            />
          )}
          <button
            type="button"
            onClick={() => (isOpen ? handleClose() : setIsOpen(true))}
            aria-label={isOpen ? 'Hide widget' : sessionActive ? 'Return to call' : (settings.buttonLabel || 'Open chat and voice widget')}
            style={{
              position: 'relative',
              width: sizeConf.button,
              height: sizeConf.button,
              borderRadius: '50%',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              background: settings.launcherColor,
              color: '#fff',
              boxShadow: `0 16px 30px -8px rgba(${launcherRgb},0.6)`,
              animation: isOpen ? undefined : 'comboV2Pop 0.4s ease-out',
              transition: 'transform 0.15s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
          >
            {isOpen ? (
              <X size={sizeConf.icon} />
            ) : settings.logoUrl ? (
              <img src={settings.logoUrl} alt="" style={{ width: sizeConf.logo, height: sizeConf.logo, objectFit: 'contain' }} />
            ) : (
              <MessageSquare size={sizeConf.icon} />
            )}
          </button>
          {sessionActive && !isOpen && (
            <span
              style={{
                position: 'absolute',
                top: -2,
                right: -2,
                width: sizeConf.badge,
                height: sizeConf.badge,
                borderRadius: '50%',
                background: '#53b59e',
                border: `2px solid ${settings.bgColor}`,
              }}
              title={voiceConnecting ? 'Connecting…' : 'Call in progress'}
            />
          )}
        </div>
      </div>
    </>
  );
};

export const ComboWidgetV2Page = ({ agentId }) => <ComboWidgetV2 agentId={agentId} />;

export default ComboWidgetV2;
